import React, { useEffect, useState, useCallback } from 'react';
import { api } from '@/lib/api';
import { Building2, ChevronDown, Check } from 'lucide-react';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
  DropdownMenuLabel, DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';
import { Button } from '@/components/ui/button';
import { useI18n } from '@/lib/i18n';

export function LocationSwitcher() {
  const { lang } = useI18n();
  const [locations, setLocations] = useState([]);
  const [current, setCurrent] = useState(localStorage.getItem('current_location_id') || '');

  const load = useCallback(async () => {
    try {
      const r = await api.get('/locations');
      setLocations(r.data);
      const stored = localStorage.getItem('current_location_id') || '';
      if (!stored && r.data.length) {
        const principal = r.data.find(l => l.es_principal) || r.data[0];
        setCurrent(principal.id);
        localStorage.setItem('current_location_id', principal.id);
        api.defaults.headers.common['X-Location-Id'] = principal.id;
      } else if (stored) {
        api.defaults.headers.common['X-Location-Id'] = stored;
      }
    } catch (e) {
      console.error('LocationSwitcher: failed to load locations', e);
    }
  }, []);

  useEffect(() => {
    load();
    const onChange = () => load();
    window.addEventListener('locations-changed', onChange);
    return () => window.removeEventListener('locations-changed', onChange);
  }, [load]);

  const switchTo = async (lid) => {
    setCurrent(lid);
    localStorage.setItem('current_location_id', lid);
    api.defaults.headers.common['X-Location-Id'] = lid;
    try {
      await api.put('/settings', { current_location_id: lid });
    } catch (e) {
      console.warn('LocationSwitcher: could not persist current_location_id (will continue with localStorage)', e);
    }
    // Refresh page to apply scoped data
    window.location.reload();
  };

  if (!locations.length || locations.length < 2) {
    // If only 1 location, show static badge
    const single = locations[0];
    if (!single) return null;
    return (
      <div className="hidden lg:inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-[hsl(var(--muted))] text-xs font-medium text-[hsl(var(--muted-foreground))]" data-testid="location-switcher-single">
        <Building2 className="h-3.5 w-3.5" />
        <span className="truncate max-w-[140px]">{single.nombre}</span>
      </div>
    );
  }

  const cur = locations.find(l => l.id === current) || locations[0];

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="outline" size="sm" className="gap-1.5" data-testid="location-switcher-button">
          <Building2 className="h-3.5 w-3.5" />
          <span className="hidden md:inline truncate max-w-[120px]">{cur?.nombre}</span>
          <ChevronDown className="h-3 w-3 opacity-60" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-64">
        <DropdownMenuLabel className="text-xs">{lang === 'es' ? 'Cambiar de local' : 'Trocar de local'}</DropdownMenuLabel>
        <DropdownMenuSeparator />
        {locations.map(loc => (
          <DropdownMenuItem key={loc.id} onClick={() => switchTo(loc.id)} data-testid={`location-option-${loc.id}`}>
            <div className="flex items-start gap-2 w-full">
              {loc.color && <span className="h-2 w-2 rounded-full mt-1.5" style={{ background: loc.color }} />}
              <div className="flex-1 min-w-0">
                <div className="font-medium text-sm truncate">{loc.nombre}</div>
                {loc.direccion && <div className="text-xs text-[hsl(var(--muted-foreground))] truncate">{loc.direccion}</div>}
              </div>
              {current === loc.id && <Check className="h-3.5 w-3.5 mt-0.5 text-[hsl(var(--primary))]" />}
              {loc.es_principal && <span className="text-[10px] uppercase tracking-wider text-[hsl(var(--primary))]">Principal</span>}
            </div>
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
